import React, { Component } from 'react';
import { Tabs, Tab, Form, Row, Col, Button } from 'react-bootstrap';
import Select from "react-select";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import 'ace-builds/webpack-resolver';
import Preferences from "context/preferences";

function getTimezoneOptions() {
  const options = [{ value: "UTC", label: "UTC" }];
  try {
    const timezones = Intl.supportedValuesOf('timeZone');
    for (const tz of timezones) {
      options.push({ value: tz, label: tz });
    }
  } catch (err) {
    // Fallback: UTC-only list
  }
  return options;
}

const TIMEZONE_OPTIONS = getTimezoneOptions();

function scopeLabel(scope) {
  if (scope && scope.blobId) return "blob";
  if (scope && scope.orgId) return "organization";
  return "user";
}

export default class PreferencesEditor extends Component {
  constructor(props) {
    super(props);

    const raw = Preferences.getRaw(this.props.scope);
    this.state = {
      timezone: Preferences.get("app.timezone", this.props.scope),
      jsonText: JSON.stringify(raw, null, 2),
      jsonValid: true,
      jsonDirty: false,
      applied: false
    };
  }

  handleTimezoneChange = (option) => {
    Preferences.set("app.timezone", option.value, this.props.scope);
    this.setState({
      timezone: option.value,
      jsonText: JSON.stringify(Preferences.getRaw(this.props.scope), null, 2),
      jsonDirty: false
    });
  }

  handleJsonChange = (newValue) => {
    let valid = true;
    try {
      JSON.parse(newValue);
    } catch (err) {
      valid = false;
    }
    this.setState({
      jsonText: newValue,
      jsonValid: valid,
      jsonDirty: true,
      applied: false
    });
  }

  handleApply = () => {
    try {
      const parsed = JSON.parse(this.state.jsonText);
      Preferences.setRaw(parsed, this.props.scope);
      this.setState({
        timezone: Preferences.get("app.timezone", this.props.scope),
        jsonDirty: false,
        applied: true
      });
    } catch (err) {
      // Should not happen since Apply is disabled when invalid
    }
  }

  render() {
    const selectedTimezone = TIMEZONE_OPTIONS.find(
      opt => opt.value === this.state.timezone
    ) || { value: "UTC", label: "UTC" };

    const level = scopeLabel(this.props.scope);

    return (
      <>
        <h5>Preferences</h5>
        <hr/>
        <div>
          Preferences defined here apply at the <strong>{level}</strong> level.
          Values set at a more specific level (user &rarr; organization &rarr; blob) take
          precedence over broader ones.
        </div>
        <div className="main-tabs">
          <Tabs defaultActiveKey="general" id="preferences-tabs" className="content-row">
            <Tab eventKey="general" title="General">
              <div className="content-row" style={{ paddingTop: "16px" }}>
                <div style={{ marginBottom: "16px" }}>
                  Common preferences with native controls. Changes are saved immediately.
                </div>
                <Form.Group>
                  <Form.Label>
                    <strong>Timezone</strong>
                  </Form.Label>
                  <Row>
                    <Col sm={4}>
                      <Select
                        options={TIMEZONE_OPTIONS}
                        value={selectedTimezone}
                        onChange={this.handleTimezoneChange}
                        placeholder="Select timezone..."
                      />
                    </Col>
                  </Row>
                  <Form.Text muted>
                    Timezone used for displaying dates and times.
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>
            <Tab eventKey="advanced" title="Advanced">
              <div className="content-row" style={{ paddingTop: "16px" }}>
                <div style={{ marginBottom: "16px" }}>
                  Raw JSON view of preferences at this level. Edit directly and click Apply to save.
                </div>
                <Row>
                  <Col sm={11}>
                    <AceEditor
                      name="preferences-json-editor"
                      width="100%"
                      height="200px"
                      mode="json"
                      theme="monokai"
                      wrapEnabled={true}
                      value={this.state.jsonText}
                      onChange={this.handleJsonChange}
                    />
                  </Col>
                </Row>
                <div className="content-row">
                  <Button
                    variant="success"
                    size="sm"
                    disabled={!this.state.jsonValid || !this.state.jsonDirty}
                    onClick={this.handleApply}
                  >
                    Apply
                  </Button>
                  {!this.state.jsonValid &&
                    <span className="text-danger" style={{ marginLeft: "12px" }}>
                      Invalid JSON
                    </span>
                  }
                  {this.state.applied &&
                    <span className="text-success" style={{ marginLeft: "12px" }}>
                      Applied
                    </span>
                  }
                </div>
                <div className="content-row">
                  <Form.Text muted>
                    Preferences are stored as flat key-value pairs with dot-notation keys.<br/>
                    Categories: <code>app.*</code> (application),
                    <code> blob:&lt;type&gt;.*</code> (blob-specific),
                    <code> custom.*</code> (user-defined).
                  </Form.Text>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </>
    );
  }
}
